import { formatMoneyIn, type Locale } from '@zuruflex/i18n';
import type { CustomerPromotion, VehiclePromoBadge } from '@zuruflex/types';
import type { TFunction } from 'i18next';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * A code somebody arrived with — `?promo=SUMMER` on a campaign link, or
 * `?ref=AMA7K2` on a friend's — held until they reach checkout.
 *
 * `sessionStorage`, like the checkout intent in `booking.ts`: a code from a
 * link is part of this visit, not a preference, and one found in a tab
 * opened next week would apply a discount nobody remembers asking for.
 * Every access is wrapped, because private windows and blocked site data
 * throw rather than returning nothing — and a failed read only means the
 * renter types the code themselves.
 */
const PROMO_KEY = 'zuruflex.promo.pending';

export function rememberPromo(code: string): void {
  try {
    sessionStorage.setItem(PROMO_KEY, code.trim().toUpperCase());
  } catch {
    // Blocked storage. The code field at checkout still takes it by hand.
  }
}

export function rememberedPromo(): string | null {
  try {
    return sessionStorage.getItem(PROMO_KEY) || null;
  } catch {
    return null;
  }
}

export function forgetPromo(): void {
  try {
    sessionStorage.removeItem(PROMO_KEY);
  } catch {
    // Nothing to do, and nothing worth failing a page load over.
  }
}

/**
 * Picks `?promo=` or `?ref=` off whatever page a link landed on, and takes
 * it back out of the address.
 *
 * Any page, not only the landing one: a shared link to a car should carry
 * the friend's code just as well. Stripped with `replace`, the way `?auth=`
 * is, so the code is not left in an address somebody copies on to a third
 * person — the friend's link is theirs to share, not every onward one.
 */
export function usePromoLandingParam(): void {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('promo') ?? params.get('ref');
    if (!code?.trim()) return;

    rememberPromo(code);
    params.delete('promo');
    params.delete('ref');
    void navigate(
      { pathname: location.pathname, search: params.toString(), hash: location.hash },
      { replace: true, preventScrollReset: true },
    );
  }, [location.pathname, location.search, location.hash, navigate]);
}

/** Basis points to the figure a percent label shows: 1250 → 12.5. */
export const bpsToPercent = (bps: number): number => bps / 100;

/** "10% off 7+ days", from the badge the API put on a car. */
export function promoBadgeLabel(t: TFunction, badge: VehiclePromoBadge, locale: Locale): string {
  return t(`common:${badge.labelKey}`, {
    percent: bpsToPercent(badge.percentBps ?? 0),
    amount: formatMoneyIn(badge.amountOff ?? 0, badge.currency, locale),
    count: badge.minDays ?? 0,
  });
}

/**
 * What an offer is worth, then what it asks of the hire — "10% off, up to
 * GH₵500 · 7+ day hires" — in the words the car badges already use, so
 * one offer does not read two ways on two pages.
 */
export function offerTerms(t: TFunction, offer: CustomerPromotion, locale: Locale): string[] {
  const money = (amount: number) => formatMoneyIn(amount, offer.currency, locale);
  const value =
    offer.discountType === 'PERCENT'
      ? t('common:promo.badge.percent', { percent: bpsToPercent(offer.percentBps ?? 0) })
      : t('common:promo.badge.amount', { amount: money(offer.amountOff ?? 0) });

  return [
    offer.discountType === 'PERCENT' && offer.maxDiscount
      ? `${value}, ${t('web:offers.upTo', { amount: money(offer.maxDiscount) })}`
      : value,
    ...(offer.minDays ? [t('web:offers.minDays', { count: offer.minDays })] : []),
    ...(offer.minSubtotal
      ? [t('web:offers.minSubtotal', { amount: money(offer.minSubtotal) })]
      : []),
  ];
}
